"use client";
import { Stack, Typography, Container, Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { ShoppingPlatformItem } from "@/types/schema";
import { ShoppingPlatformCard } from "./shopping_platform_card";
import { DividerGradient } from "../divider_gradient";
import GlowButton from "./glow_button";

const GradientText = styled(Typography)`
  background: linear-gradient(
    90deg,
    rgba(205, 120, 88, 1) 0%,
    rgba(255, 217, 105, 1) 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-family: "Noto Sans Thai", sans-serif;
`;

interface ShoppingPlatformsSectionProps {
  platforms: ShoppingPlatformItem[];
}

export function ShoppingPlatformsSection({
  platforms,
}: ShoppingPlatformsSectionProps) {
  return (
    <Stack
      px={{
        xs: 4,
        md: 10,
      }}
      py={{
        xs: 10,
        md: 15,
      }}
    >
      <Stack
        borderRadius={4}
        overflow="hidden"
        sx={{
          backgroundImage: {
            xs: `url(/images/platforms/bg_shopping_platforms_mobile.png)`,
            md: `url(/images/platforms/bg_shopping_platforms.webp)`,
          },
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
        position="relative"
        zIndex={2}
        gap={{
          xs: 12,
          md: 15,
        }}
        py={{
          xs: 12,
          md: 20,
        }}
        px={{
          xs: 4,
          md: 8,
        }}
        alignItems="center"
      >
        {/* Header Section */}
        <Stack gap={{ xs: 2, md: 2 }} alignItems="center" direction='row' sx={{ position: 'relative' }}>
          <GradientText
            sx={{
              fontSize: {
                xs: "32px",
                md: "48px",
              },
              fontWeight: 600,
              lineHeight: {
                xs: "38px",
                md: "56px",
              },
              textAlign: "center",
            }}
          >
            ช่องทางการสั่งซื้อ
          </GradientText>

          <Box sx={{
            position: 'absolute',
            top: {
              xs: '1em',
              md: '-1.5em',
            },
            right: {
              xs: '2em',
              md: '-10em',
            },
          }}>
            <GlowButton />
          </Box>
        </Stack>

        {/* Platforms Grid */}
        <Stack
          direction={{
            xs: "column",
            md: "row",
          }}
          gap={{
            xs: 6,
            md: 7,
          }}
          alignItems="center"
          justifyContent="center"
          width="100%"
          sx={{
            // Mobile: 2x2 grid layout
            display: {
              xs: "flex",
              md: "flex",
            },
            flexDirection: {
              xs: "column",
              md: "row",
            },
          }}
        >
          {/* Mobile: First Row */}
          <Stack
            direction="row"
            gap={7}
            alignItems="center"
            justifyContent="center"
            sx={{
              display: {
                xs: "flex",
                md: "none",
              },
            }}
          >
            <Box sx={{ width: 140, display: "flex", justifyContent: "center" }}>
              <ShoppingPlatformCard platform={platforms[0]} />
            </Box>
            <DividerGradient isVertical={true} height="95px" />
            <Box sx={{ width: 140, display: "flex", justifyContent: "center" }}>
              <ShoppingPlatformCard platform={platforms[1]} />
            </Box>
          </Stack>

          {/* Mobile: Second Row */}
          <Stack
            direction="row"
            gap={7}
            alignItems="center"
            justifyContent="center"
            sx={{
              display: {
                xs: "flex",
                md: "none",
              },
            }}
          >
            <Box sx={{ width: 140, display: "flex", justifyContent: "center" }}>
              <ShoppingPlatformCard platform={platforms[2]} />
            </Box>
            <DividerGradient isVertical={true} height="95px" />
            <Box sx={{ width: 140, display: "flex", justifyContent: "center" }}>
              <ShoppingPlatformCard platform={platforms[3]} />
            </Box>
          </Stack>
          {/* Mobile: Third Row */}
          <Stack
            direction="row"
            gap={7}
            alignItems="center"
            justifyContent="center"
            sx={{
              display: {
                xs: "flex",
                md: "none",
              },
            }}
          >
            <Box sx={{ width: 140, display: "flex", justifyContent: "center" }}>
              <ShoppingPlatformCard platform={platforms[4]} />
            </Box>
            <DividerGradient isVertical={true} height="95px" />
            <Box sx={{ width: 140, display: "flex", justifyContent: "center" }} />
          </Stack>

          <Stack
            direction="row"
            sx={{
              position: "relative",
              display: {
                xs: "none",
                md: "flex",
              },
            }}
            gap={7}
          >
            {/* Desktop: Single Row */}
            {platforms.map((platform, index) => (
              <Stack
                direction="row"
                key={platform.id}
                gap={7}
                alignItems="center"
              >
                <ShoppingPlatformCard platform={platform} />
                {index < platforms.length - 1 && (
                  <DividerGradient isVertical={true} height="50%" />
                )}
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}
